export const subject = {
  org: 'org',
  app: 'app',
  role: 'role',
  user: 'user',
  all: 'all',
} as const;

export const action = {
  all: {
    manage: 'manage',
    read: 'read',
    create: 'create',
    update: 'update',
    delete: 'delete',
  },
  org: {
    manage: 'manage',
    read: 'read',
    create: 'create',
    update: 'update',
    delete: 'delete',
  },
  app: {
    manage: 'manage',
    read: 'read',
    create: 'create',
    update: 'update',
    delete: 'delete',
    assign: 'assign',
    unassign: 'unassign',
  },
  role: {
    manage: 'manage',
    read: 'read',
    create: 'create',
    update: 'update',
    delete: 'delete',
    assign: 'assign',
    unassign: 'unassign',
  },
  user: {
    manage: 'manage',
    read: 'read',
    create: 'create',
    update: 'update',
    delete: 'delete',
    invite: 'invite',
    remove: 'remove',
  },
} as const satisfies Record<Subject, Record<string, string>>;

export const permission = {
  all: {
    manage: 'all:manage',
    read: 'all:read',
    create: 'all:create',
    update: 'all:update',
    delete: 'all:delete',
  },
  org: {
    manage: 'org:manage',
    read: 'org:read',
    create: 'org:create',
    update: 'org:update',
    delete: 'org:delete',
  },
  app: {
    manage: 'app:manage',
    read: 'app:read',
    create: 'app:create',
    update: 'app:update',
    delete: 'app:delete',
    assign: 'app:assign',
    unassign: 'app:unassign',
  },
  role: {
    manage: 'role:manage',
    read: 'role:read',
    create: 'role:create',
    update: 'role:update',
    delete: 'role:delete',
    assign: 'role:assign',
    unassign: 'role:unassign',
  },
  user: {
    manage: 'user:manage',
    read: 'user:read',
    create: 'user:create',
    update: 'user:update',
    delete: 'user:delete',
    invite: 'user:invite',
    remove: 'user:remove',
  },
} as const satisfies PermissionObject;

export type Subject = keyof typeof subject;
export type SubjectValue<S extends Subject = Subject> = (typeof subject)[S];

// Define the action type for a specific subject
export type Action<S extends Subject> = keyof (typeof action)[S] & string;

// Generic type to extract permission string format for any subject and action
export type PermissionString<
  S extends Subject = Subject,
  A extends Action<S> = Action<S>,
  SV extends SubjectValue<S> = SubjectValue<S>,
> = `${SV}:${A}` | `${SV}:${A}?${string}` | `!${SV}:${A}!${string}` | `!${SV}:${A}`;

// Type for values of the permission object
export type PermissionObject = {
  [S in Subject]: {
    [A in Action<S>]: PermissionString<S, A>;
  };
};
